import React from 'react';
import BeatLoader from 'react-spinners/BeatLoader';
import { MdClose, MdOutlineConfirmationNumber, MdOutlineRefresh } from 'react-icons/md';
import CustomDialog from '../../../components/CustomDialog';

const toNumber = (value) => Number(value) || 0;

const formatAmount = (value) =>
    toNumber(value).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const getVoucherDetails = (voucher = {}) => {
    if (Array.isArray(voucher)) {
        return voucher;
    }

    const details = voucher?.viewDetails
        || voucher?.detailsResponse
        || voucher?.details
        || voucher?.records
        || voucher?.items
        || voucher?.data;

    if (Array.isArray(details)) {
        return details;
    }

    return details && details !== voucher ? getVoucherDetails(details) : [];
};

const getStatusClass = (status = '') => {
    const normalized = String(status).toLowerCase();

    if (normalized.includes('lose')) {
        return 'bg-red-50 text-red-600 ring-red-100 dark:bg-red-500/10 dark:text-red-300 dark:ring-red-400/20';
    }

    if (normalized.includes('win')) {
        return 'bg-emerald-50 text-emerald-600 ring-emerald-100 dark:bg-emerald-500/10 dark:text-emerald-300 dark:ring-emerald-400/20';
    }

    return 'bg-amber-50 text-amber-700 ring-amber-100 dark:bg-amber-500/10 dark:text-amber-200 dark:ring-amber-400/20';
};

const getBetLabel = (row = {}) => {
    if (row.under) {
        return 'Under';
    }

    if (row.overs) {
        return 'Over';
    }

    return row.bettedTeam || '-';
};

const getBetClass = (row = {}) => {
    if (row.under) {
        return 'text-emerald-600 dark:text-emerald-300';
    }

    if (row.overs) {
        return 'text-red-500 dark:text-red-300';
    }

    if((row.isHomeBodyOdd && row.home) || (!row.isHomeBodyOdd && row.away)) {
        return 'text-red-500 dark:text-red-300';
    }

    return 'text-green-700 dark:text-emerald-300';
};

const getMatchName = (row = {}) => {
    if (row.isHome) {
        return `${row.bettedTeam || '-'} VS ${row.oppositeTeamName || '-'}`;
    }

    return `${row.oppositeTeamName || '-'} VS ${row.bettedTeam || '-'}`;
};

const SummaryItem = ({ label, value, tone = 'default' }) => {
    const toneClass = {
        default: 'text-slate-900 dark:text-customWhite',
        win: 'text-emerald-600 dark:text-emerald-300',
        lose: 'text-red-600 dark:text-red-300',
    }[tone];

    return (
        <div className="rounded-md border border-slate-200 bg-white px-3 py-2 shadow-sm dark:border-white/10 dark:bg-custom_dark_primary2">
            <p className="text-[0.68rem] font-semibold uppercase tracking-wide text-slate-500 dark:text-customMuted">{label}</p>
            <p className={`mt-1 text-sm font-bold ${toneClass}`}>{value}</p>
        </div>
    );
};

const WinLoseVoucherDialog = ({ open, voucher, loading, error, onClose, onRetry }) => {
    const details = getVoucherDetails(voucher);
    const voucherStatus = voucher?.voucherStatus || voucher?.status || '-';
    const result = toNumber(voucher?.result ?? voucher?.netResult);
    const betAmount = voucher?.betAmount ?? voucher?.amount ?? voucher?.stake ?? voucher?.details?.stake;
    const loseAmount = voucher?.loseAmount ?? voucher?.lostAmount ?? (result < 0 ? Math.abs(result) : 0);
    const winAmount = voucher?.winAmount ?? (result > 0 ? result : 0);
    const voucherNumber = voucher?.voucherNo || voucher?.postingNo || voucher?.posting || voucher?.id || '-';

    return (
        <CustomDialog
            open={open}
            onClose={onClose}
            width={700}
            padding={0}
            extraStyle={{
                border: '0',
                borderRadius: '10px',
                maxHeight: '88vh',
                maxWidth: 'calc(100vw - 24px)',
                overflow: 'hidden',
            }}
        >
            <div className="bg-slate-50 text-slate-900 dark:bg-custom_dark_background dark:text-customWhite">
                <div className="flex items-start justify-between gap-4 border-b border-slate-200 bg-white px-4 py-3 dark:border-white/10 dark:bg-custom_dark_primary2">
                    <div className="min-w-0">
                        <div className="flex items-center gap-2">
                            <span className="inline-flex h-9 w-9 items-center justify-center rounded-md bg-customAccentBlue/10 text-customAccentBlue dark:bg-customAccent/20 dark:text-customAccent">
                                <MdOutlineConfirmationNumber className="text-xl" />
                            </span>
                            <div className="min-w-0">
                                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-customMuted">Voucher details</p>
                                <p className="truncate text-base font-bold">{voucherNumber}</p>
                            </div>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <span className={`rounded-full px-3 py-1 text-xs font-bold ring-1 ${getStatusClass(voucherStatus)}`}>
                            {voucherStatus}
                        </span>
                        <button
                            type="button"
                            className="inline-flex h-9 w-9 items-center justify-center rounded-md border border-slate-200 text-slate-500 transition hover:bg-slate-100 hover:text-slate-900 dark:border-white/10 dark:text-customWhite dark:hover:bg-white/10"
                            onClick={onClose}
                            aria-label="Close voucher details"
                            title="Close"
                        >
                            <MdClose className="text-xl" />
                        </button>
                    </div>
                </div>

                <div className="space-y-3 p-4">
                    <div className="grid gap-2 sm:grid-cols-3">
                        <SummaryItem label="Bet amount" value={formatAmount(betAmount)} />
                        <SummaryItem label="Win amount" value={formatAmount(winAmount)} tone="win" />
                        <SummaryItem label="Lose amount" value={formatAmount(loseAmount)} tone="lose" />
                    </div>

                    <div className="overflow-hidden rounded-md border border-slate-200 bg-white shadow-sm dark:border-white/10 dark:bg-custom_dark_primary2">
                        {loading ? (
                            <div className="flex min-h-[260px] flex-col items-center justify-center gap-3">
                                <BeatLoader color="#2563eb" loading={loading} size={14} aria-label="Loading voucher details" />
                                <p className="text-sm font-semibold text-slate-500 dark:text-customMuted">Loading voucher details...</p>
                            </div>
                        ) : error ? (
                            <div className="flex min-h-[220px] flex-col items-center justify-center gap-3 px-4 text-center">
                                <p className="text-sm font-semibold text-red-600 dark:text-red-300">{error}</p>
                                <button
                                    type="button"
                                    className="inline-flex items-center gap-2 rounded-md bg-customAccentBlue px-4 py-2 text-sm font-semibold text-white transition hover:bg-customAccentBlue/85 dark:bg-customAccent dark:text-customHeading"
                                    onClick={onRetry}
                                >
                                    <MdOutlineRefresh className="text-lg" />
                                    Retry
                                </button>
                            </div>
                        ) : details.length ? (
                            <div className="max-h-[52vh] overflow-auto">
                                <table className="w-full min-w-[400px] text-sm">
                                    <thead className="sticky top-0 z-10 bg-slate-800 text-white dark:bg-custom_dark_secondary">
                                        <tr>
                                            <th className="w-14 px-3 py-3 text-left text-xs font-bold">No</th>
                                            <th className="px-3 py-3 text-left text-xs font-bold">Choice</th>
                                            <th className="w-24 px-3 py-3 text-center text-xs font-bold">Odds</th>
                                            <th className="w-44 px-3 py-3 text-right text-xs font-bold">Bet</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-200 text-xs font-semibold dark:divide-white/10">
                                        {details.map((row, index) => (
                                            <tr key={`${row.rapidEventId || row.eventId || row.leagueName}-${index}`} className="transition hover:bg-slate-50 dark:hover:bg-white/5">
                                                <td className="px-3 py-3 align-top text-slate-600 dark:text-customMuted">{index + 1}</td>
                                                <td className="px-3 py-3">
                                                    <div className="space-y-1">
                                                        <p className="font-bold text-blue-600 dark:text-sky-300">{row.leagueName || '-'}</p>
                                                        <p className="text-slate-700 dark:text-customLightMilkWhite">{getMatchName(row)}</p>
                                                        <p className="font-bold text-customFontVoilet dark:text-customAccent">
                                                            Result ({row.homeResult ?? '-'} - {row.awayResult ?? '-'})
                                                        </p>
                                                    </div>
                                                </td>
                                                <td className="px-3 py-3 text-center align-top text-slate-700 dark:text-customLightMilkWhite">
                                                    {row.overs || row.under ? row.goalOdd : row.bodyOdd}
                                                </td>
                                                <td className={`px-3 py-3 text-right align-top font-bold ${getBetClass(row)}`}>
                                                    {getBetLabel(row)}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        ) : (
                            <div className="flex min-h-[220px] items-center justify-center px-4 text-center">
                                <p className="text-sm font-semibold text-slate-500 dark:text-customMuted">No voucher details found.</p>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </CustomDialog>
    );
};

export default WinLoseVoucherDialog;
